import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AppHeader from "../components/AppHeader";
import { checklistApi } from "../utils/Api";
import {
    formatBusinessDate,
    formatDateTime,
    labelize,
} from "../utils/format";

function normalizeDetail(data) {
    const root = data && typeof data === "object" ? data : {};
    const submission = root.submission || root;
    let categories = root.categories || submission.categories || [];

    if (!Array.isArray(categories)) categories = [];

    if (!categories.length && Array.isArray(submission.answers || root.answers)) {
        const answers = submission.answers || root.answers;
        const grouped = new Map();

        answers.forEach((answer) => {
            const key = answer.category_id || answer.category_name || "Checklist";

            if (!grouped.has(key)) {
                grouped.set(key, {
                    category_id: answer.category_id,
                    category_name: answer.category_name || "Checklist",
                    answers: [],
                });
            }

            grouped.get(key).answers.push(answer);
        });

        categories = Array.from(grouped.values());
    }

    const summary = root.summary || submission.summary || null;

    return {
        submission,
        summary,
        categories,
        readOnly: root.read_only ?? submission.read_only,
        isOwn: root.is_own_submission ?? submission.is_own_submission,
    };
}

function answerRows(category) {
    if (Array.isArray(category.answers)) return category.answers;
    if (Array.isArray(category.tasks)) return category.tasks;
    return [];
}

export default function SubmissionDetail() {
    const navigate = useNavigate();
    const { id } = useParams();
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        let active = true;

        const load = async () => {
            try {
                setLoading(true);
                setError("");
                const json = await checklistApi.getSubmission(id);

                if (!active) return;

                setDetail(normalizeDetail(json?.data));
            } catch (loadError) {
                if (!active || loadError?.status === 401) return;

                setError(loadError?.message || "Unable to open this submission.");
            } finally {
                if (active) setLoading(false);
            }
        };

        load();

        return () => {
            active = false;
        };
    }, [id]);

    const submission = detail?.submission || {};
    const answers = detail?.categories?.flatMap((category) => answerRows(category)) || [];
    const yesCount =
        detail?.summary?.yes_count ??
        answers.filter((answer) => String(answer.answer).toUpperCase() === "YES").length;
    const noCount =
        detail?.summary?.no_count ??
        answers.filter((answer) => String(answer.answer).toUpperCase() === "NO").length;

    return (
        <main className="min-h-screen bg-gradient-to-b from-[#18c2dc] via-[#08a8cc] to-[#017fa8] flex justify-center font-sans">
            <section className="relative w-full max-w-[430px] min-h-screen bg-[#F6FBFD] shadow-[0_24px_70px_rgba(14,73,97,0.26)] overflow-hidden">
                <AppHeader />

                <div className="px-5 pt-5 pb-10">
                    <button
                        type="button"
                        onClick={() => navigate("/history")}
                        className="text-[#09A5F7] text-sm font-extrabold mb-5"
                    >
                        ← Back
                    </button>

                    {loading ? (
                        <div className="rounded-[24px] bg-white border border-[#D7EEF6] p-5 text-center">
                            <div className="w-9 h-9 mx-auto rounded-full border-[3px] border-[#09A5F7]/20 border-t-[#09A5F7] animate-spin" />
                            <p className="mt-3 text-sm font-extrabold text-[#0E4961]">
                                Loading submission...
                            </p>
                        </div>
                    ) : error ? (
                        <div className="rounded-[24px] bg-red-50 border border-red-100 p-5">
                            <p className="text-sm font-extrabold text-red-500">{error}</p>
                        </div>
                    ) : (
                        <>
                            <div className="rounded-[28px] bg-white border border-[#DDF0F6] p-5 shadow-sm">
                                <p className="text-[11px] uppercase tracking-[0.16em] text-[#09A5F7] font-black">
                                    {labelize(submission.checklist_type)}
                                    {submission.frequency
                                        ? ` · ${labelize(submission.frequency)}`
                                        : ""}
                                </p>
                                <h1 className="text-[22px] font-black text-[#0E4961] mt-2">
                                    {formatBusinessDate(submission.business_date) ||
                                        "Submission"}
                                </h1>
                                <p className="text-sm font-bold text-[#6E8794] mt-2">
                                    {labelize(submission.status) || "Saved"}
                                    {submission.submitted_at
                                        ? ` · ${formatDateTime(submission.submitted_at)}`
                                        : ""}
                                </p>
                                {(submission.submitted_by_name ||
                                    submission.staff_name ||
                                    submission.submitted_by) && (
                                    <p className="text-xs font-bold text-[#0E4961] mt-2">
                                        {detail.isOwn
                                            ? "Submitted by you"
                                            : `Submitted by ${
                                                  submission.submitted_by_name ||
                                                  submission.staff_name ||
                                                  submission.submitted_by
                                              }`}
                                    </p>
                                )}
                                {detail.readOnly && (
                                    <p className="mt-3 text-xs font-bold text-amber-700">
                                        This submission is view only.
                                    </p>
                                )}

                                <div className="mt-4 grid grid-cols-2 gap-3">
                                    <div className="rounded-2xl bg-green-50 border border-green-100 px-4 py-3">
                                        <p className="text-[11px] font-bold text-green-600">
                                            YES
                                        </p>
                                        <p className="text-2xl font-black text-green-600">
                                            {yesCount}
                                        </p>
                                    </div>
                                    <div className="rounded-2xl bg-red-50 border border-red-100 px-4 py-3">
                                        <p className="text-[11px] font-bold text-red-500">
                                            NO
                                        </p>
                                        <p className="text-2xl font-black text-red-500">
                                            {noCount}
                                        </p>
                                    </div>
                                </div>

                                {(submission.address ||
                                    (submission.latitude && submission.longitude)) && (
                                    <p className="mt-4 text-xs font-medium text-[#6E8794] leading-relaxed">
                                        📍{" "}
                                        {submission.address ||
                                            `${submission.latitude}, ${submission.longitude}`}
                                    </p>
                                )}
                            </div>

                            <div className="mt-5 space-y-4">
                                {detail.categories.map((category, categoryIndex) => (
                                    <div
                                        key={category.category_id || category.id || categoryIndex}
                                        className="rounded-[24px] bg-white border border-[#DDF0F6] p-4"
                                    >
                                        <h2 className="text-[#0E4961] font-extrabold">
                                            {category.category_name ||
                                                category.name ||
                                                "Checklist"}
                                        </h2>
                                        <div className="mt-3 space-y-3">
                                            {answerRows(category).map((answer, index) => {
                                                const value = String(
                                                    answer.answer || ""
                                                ).toUpperCase();

                                                return (
                                                    <div
                                                        key={answer.task_id || answer.id || index}
                                                        className="rounded-[18px] bg-[#F7FBFD] border border-[#E7F4F8] px-3 py-3"
                                                    >
                                                        <div className="flex items-start justify-between gap-3">
                                                            <p className="text-sm font-bold text-[#0E4961] leading-snug">
                                                                {answer.task_name ||
                                                                    answer.title ||
                                                                    `Task ${index + 1}`}
                                                            </p>
                                                            <span
                                                                className={`shrink-0 rounded-full px-2 py-1 text-[10px] font-black ${
                                                                    value === "YES"
                                                                        ? "bg-green-100 text-green-600"
                                                                        : value === "NO"
                                                                          ? "bg-red-100 text-red-500"
                                                                          : "bg-slate-100 text-slate-500"
                                                                }`}
                                                            >
                                                                {value || "—"}
                                                            </span>
                                                        </div>
                                                        {answer.remark && (
                                                            <p className="mt-2 text-xs text-[#6E8794] leading-relaxed">
                                                                Remark: {answer.remark}
                                                            </p>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ))}

                                {detail.categories.length === 0 && (
                                    <div className="rounded-[24px] bg-white border border-[#DDF0F6] p-5 text-center">
                                        <p className="text-sm font-extrabold text-[#0E4961]">
                                            No answers were returned for this submission.
                                        </p>
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </section>
        </main>
    );
}
